import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import { clearSiparisTaslak, fetchSiparisTaslakOzetleri } from '../services/siparisTaslakService';
import './SiparisTaslaklariPanel.css';

const formatTarih = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
};

const SiparisTaslaklariPanel = ({ onDevam, onCleared, refreshKey = 0 }) => {
  const { confirm } = useConfirmDialog();
  const [ozetler, setOzetler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearingTip, setClearingTip] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchSiparisTaslakOzetleri();
      setOzetler(list);
    } catch {
      setOzetler([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleTaslakTemizle = async (o) => {
    if (!o.dolu) return;
    const ok = await confirm({
      title: 'Taslağı temizle',
      message: `${o.shortLabel} taslağındaki kayıtlı parçalar ve notlar silinecek. Silmek istediğinizden emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
      cancelLabel: 'Vazgeç',
    });
    if (!ok) return;
    setClearingTip(o.tip);
    try {
      await clearSiparisTaslak(o.tip);
      window.showNotification?.('success', `${o.shortLabel} taslağı temizlendi`);
      await load();
      onCleared?.(o.tip);
    } catch (err) {
      window.showNotification?.('error', err?.message || 'Taslak temizlenemedi');
    } finally {
      setClearingTip(null);
    }
  };

  return (
    <section className="siparis-taslaklari-section" aria-label="Sipariş taslakları">
      <div style={{ marginBottom: '12px' }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 4px' }}>Sipariş taslakları</h2>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-gray)' }}>
          Parça ekleme/çıkarma ve sipariş adımları otomatik kaydedilir. Yanlışlıkla çıkarsanız «Sipariş ver» ile kaldığınız yerden devam edebilirsiniz.
        </p>
      </div>
      {loading ? (
        <p style={{ fontSize: '14px', color: 'var(--text-gray)' }}>Taslaklar yükleniyor…</p>
      ) : (
        <div className="siparis-taslaklari">
          {ozetler.map((o) => {
            const tarih = formatTarih(o.guncellenmeTarihi);
            const metaParts = [];
            if (o.parcaAdet > 0) metaParts.push(`${o.parcaAdet} parça`);
            if (o.photoCount > 0) metaParts.push(`${o.photoCount} fotoğraf`);
            if (!metaParts.length) metaParts.push('Boş taslak');
            return (
              <div key={o.tip} className="siparis-taslak-kart">
                <div className="siparis-taslak-kart__head">
                  <span className="siparis-taslak-kart__icon" style={{ color: o.color }}>
                    <i className={`fas ${o.icon}`} aria-hidden />
                  </span>
                  <div>
                    <h3 className="siparis-taslak-kart__title">{o.shortLabel}</h3>
                    <p className="siparis-taslak-kart__meta">
                      {metaParts.join(' · ')}
                      {tarih ? ` · ${tarih}` : ''}
                    </p>
                  </div>
                </div>
                {o.dolu && <span className="siparis-taslak-kart__badge">Taslak dolu</span>}
                <div className="siparis-taslak-kart__actions">
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={() => onDevam(o.tip)}
                  >
                    <i className="fas fa-play" style={{ marginRight: '6px' }} aria-hidden />
                    Sipariş ver
                  </button>
                  <button
                    type="button"
                    className="secondary-btn siparis-taslak-kart__clear-btn"
                    disabled={!o.dolu || clearingTip === o.tip}
                    onClick={() => handleTaslakTemizle(o)}
                  >
                    {clearingTip === o.tip ? (
                      <>
                        <i className="fas fa-spinner fa-spin" aria-hidden /> Temizleniyor…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-trash-alt" style={{ marginRight: '6px' }} aria-hidden />
                        Taslağı temizle
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default SiparisTaslaklariPanel;
